// https://github.com/reach/reach-ui/blob/dev/packages/rect/src/reach-rect.tsx
// Copyright (c) 2018-2022, React Training LLC, MIT License
import type { ObserveRectOptions } from "../dom/observe-rect";

import * as React from "react";

import { observeRect, rectChanged } from "../dom/observe-rect";
import { useEffectEvent } from "./use-effect-event";
import { unsafe_useLayoutEffectWithoutWarning as useLayoutEffect } from "./use-layout-effect";

function useRect<T extends Element = HTMLElement>(
  element: T | undefined | null,
  options: UseRectOptions & { defaultValue: DOMRect },
): DOMRect;
function useRect<T extends Element = HTMLElement>(
  element: T | undefined | null,
  options?: UseRectOptions,
): null | DOMRect;

/**
 * Get the DOM rect of a DOM element. This hook will return `null` until the DOM
 * element is mounted and has a valid `getBoundingClientRect` value.
 *
 * @param element Reference to a DOM element. NOTE (Chance 2024-01-18): **It is
 * very important that this value is not a ref's current property.** Refs do not
 * trigger updates when their current property changes, so if an element is
 * removed from the DOM or changed for some reason the hook may not update.
 * Unless the element is already kept in state, either via a Redux store or
 * React state, you can use `useStatefulElement` to get back a stateful
 * reference to an element that is safe to use here.
 * @param options Options to configure the hook.
 * @param options.observe Declares which properties of the DOM rect to observe.
 * Pass `true` or `"all"` to observe all properties or `false` to skip observing
 * entirely. Defaults to `true`.
 */
function useRect<T extends Element = HTMLElement>(
  element: T | undefined | null,
  options?: UseRectOptions,
): null | DOMRect {
  const { onChange, observe, defaultValue } = options || {};
  const observeType = (() => {
    if (observe === true) {
      return "all";
    }
    if (typeof observe === "string") {
      return observe;
    }
    return false;
  })();

  const [rect, setRect] = React.useState<DOMRect | null>(defaultValue ?? null);
  const _onChange = useEffectEvent(onChange);

  useLayoutEffect(() => {
    if (!element) {
      return;
    }

    setRect((rect) => {
      const next = element.getBoundingClientRect();
      if (!rect) {
        return next;
      }
      return rectChanged(rect, next) ? next : rect;
    });

    if (!observeType) {
      return;
    }

    const observer = observeRect(
      element,
      (rect) => {
        _onChange(rect);
        setRect(rect);
      },
      { type: observeType },
    );

    observer.observe();
    return () => {
      observer.unobserve();
    };
  }, [observeType, _onChange, element]);

  return rect;
}

interface UseRectOptions {
  /**
   * Tells `Rect` to observe the position of the node or not. While observing,
   * the `children` render prop may call back very quickly (especially while
   * scrolling) so it can be important for performance to avoid observing when
   * you don't need to.
   *
   * This is typically used for elements that pop over other elements (like a
   * dropdown menu), so you don't need to observe all the time, only when the
   * popup is active.
   *
   * Pass `true` to observe, `false` to ignore. You can also pass `"position"`
   * or `"size"` to only observe the changes related to properties related to
   * one or the other.
   */
  observe?: boolean | ObserveRectOptions["type"];
  /**
   * Fires whenever the `rect` of the element changes.
   *
   * **IMPORTANT:** This function should be memoized to prevent attaching and
   * detaching observers on every render.
   */
  onChange?: (rect: DOMRect) => void;
  defaultValue?: DOMRect;
}
export { useRect };
